import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { FileError, useDropzone } from 'react-dropzone';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiDownload, BiPlay, BiTrash, BiUpload, BiVideo, BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { useGenerateUploadUrlMutation, useGetCdnFileInfoMutation, useUploadToCDNMutation } from '@/app/services/chatbot-rtk.services';
import { PSButton } from '@/components/ui/button';
import { PSInput } from '@/components/ui/input';
import { AlertsType } from '@/constants/alerts.constant';
import { selectUseId, selectUserProfile } from '@/features/auth/auth.slice';
import { selectClient } from '@/features/dashboard.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';
import { BotTypeEnum } from '@/utils/BotTypeEnum';
import { DEFAULT_VIDEO_SIZE_BYTES, resolveVideoFileSizeLimit, selectVideoScenarioPermission, toMegabytes } from '@/utils/uploadLimits';

import InfoMessageAlert from '../../../../../features/bot/InfoMessageAlert';
import Utilities from '../../../../../utils';
import HintBlock from '../../../../common/HintBlock';
import { createRandomString, isAlphabetic } from '../../../../tools';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import VideoCameraComponent from './video-camera.component';
import VideoScenarioDeleteButton from './video-scenario-delete-button';
import ScenarioSide from './video-scenario-side';

interface IResponseForm extends INoteForm {
  BlockTitle: string;
  BlockVideoPath: string;
  BlockVideoName: string;
}

export const VideoScenarioResponseForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow?: (item: boolean) => void;
  itemData: any;
  parentId: string | null;
  tree: any[];
  loading: boolean;
  createScenarioBlock: (block: any, file?: File) => void;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any, file: any) => void;
}) => {
  const { t } = useTranslation();

  const { user_identification } = useAppSelector(selectUserProfile);
  const userId = useAppSelector(selectUseId);
  const videoRef = useRef<any>(null);

  const [cdnUpload, cdnUploadResults] = useUploadToCDNMutation();

  const [showButtonSetting, setShowButtonSetting] = useState(false);
  const [selectedButtonToEdit, setSelectedButtonToEdit] = useState<number>(-1);
  const [quickReplies, setQuickReplies] = useState<any[]>([]);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(false);
  const [file, setFile] = useState<File | undefined | any>();
  const [openCamera, setOpenCamera] = useState(false);
  const [loadingProcesssIndex, setLoadingProcesssIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const profile = useAppSelector(selectUserProfile);
  const client = useAppSelector(selectClient);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(loading);

  const [video, setVideo] = useState<any>(['.mp4', '.mpeg', '.webm', '.ogg', '.mov', '.m4v', '.mkv', '.avi', '.3gp']);

  const formatExtensions: Record<string, string[]> = {
    video: video,
  };

  const LoadingProcessText = [
    <div key={0} className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
      {t('VideoScenarioResponseForm/key1')}
    </div>,
    <div key={1} className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
      {t('VideoScenarioResponseForm/key2')}
    </div>,
    <div key={2} className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
      {t('VideoScenarioResponseForm/key3')}
    </div>,
  ];

  const permissions = selectVideoScenarioPermission(useAppSelector(state => (state.dashboardState as any).Client.botConfigs));

  const fileMaxSize = resolveVideoFileSizeLimit(permissions);

  const schema = Yup.object({
    BlockTitle: Yup.string().required(t(AlertsType.ISKBKO025)),
    BlockVideoPath: Yup.string().required(t(AlertsType.ISKBKO026)),
    BlockVideoName: Yup.string().required(t(AlertsType.ISKBKO026)),
    ...getNoteSchema(t),
  });

  const {
    reset,
    setValue,
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setError,
    clearErrors,
    control,
    getValues,
  } = useForm<IResponseForm>({
    defaultValues: {
      BlockTitle: itemData?.title ?? '',
      BlockVideoPath: itemData?.path ?? '',
      BlockVideoName: itemData?.name ?? '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
    resetOptions: {
      keepDefaultValues: true,
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });
  const { fields } = useFieldArray({
    control,
    name: 'notes',
  });

  const onDrop = useCallback((acceptedFiles: any) => {
    setFile(
      Object.assign(acceptedFiles[0], {
        preview: URL.createObjectURL(acceptedFiles[0]),
      })
    );

    if (acceptedFiles[0]) {
      setValue('BlockVideoName', acceptedFiles[0].path);
      setValue('BlockVideoPath', acceptedFiles[0].preview);

      clearErrors();
    }
  }, []);

  const [acceptFiles, setAcceptFiles] = useState<any>({
    'video/mp4': ['.mp4'],
    'video/mpeg': ['.mpeg'],
    'video/webm': ['.webm'],
    'video/ogg': ['.ogg'],
    'video/mov': ['.mov'],
    'video/m4v': ['.m4v'],
    'video/mkv': ['.mkv'],
    'video/avi': ['.avi'],
    'video/3gp': ['.3gp'],
  });

  useEffect(() => {
    if (fileMaxSize > DEFAULT_VIDEO_SIZE_BYTES) {
      setAcceptFiles({
        'video/mp4': ['.mp4'],
      });

      setVideo(['.mp4']);
    }
  }, [fileMaxSize]);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxSize: fileMaxSize,
    maxFiles: 1,
    accept: acceptFiles,
    validator: file => {
      if (file.type) {
        const condition = Utilities.isSupportedVideoFormatMimeType(file.type);
        if (condition) {
          return null;
        } else {
          return {
            message: t('VideoMacBotResponseForm/text-03'),
          } as FileError;
        }
      } else {
        const condition = Utilities.isSupportedVideoFormatWithFileName(file.name);
        if (condition) {
          return null;
        } else {
          return {
            message: t('VideoMacBotResponseForm/text-03'),
          } as FileError;
        }
      }
    },
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
  };

  const removeButtonHandler = (btnIndex: number) => {
    setQuickReplies(prev => prev.filter((p, index) => index !== btnIndex));
  };

  const addButtonHandler = (qBtn: number) => {
    setQuickReplies(prev => [...prev, qBtn]);
  };

  const editButtonHandler = (_index: number, qRep: any) => {
    setQuickReplies(prev => prev.map((p, index) => (index === _index ? qRep : p)));

    setSelectedButtonToEdit(-1);
  };

  const closeButtonSettingModal = () => {
    setShowButtonSetting(false);
    setSelectedButtonToEdit(-1);
  };

  const createTitle = (text: string) => {
    let titleCode = 0;

    tree.forEach(element => {
      if (element.type === BlockType.VideoBlock) {
        const _code = element.title.split('-');
        if (_code[0] && Number(_code[0]) > titleCode) titleCode = +_code[0];
      }
    });

    if (typeof text != 'string' || !text) {
      return '';
    }
    let acronym = text
      ?.match(/[\p{Alpha}\p{Nd}]+/gu)
      ?.reduce((previous, next) => previous + (+next === 0 || parseInt(next) ? parseInt(next) : next[0] || ''), '')
      ?.toUpperCase();

    if (!acronym || (acronym && acronym.length < 3) || !isAlphabetic(acronym)) {
      acronym = createRandomString(5);
    }

    return `${titleCode + 1}-${acronym?.slice(0, 3)}`;
  };

  function convertBytesToMB(file: File) {
    if (!file) return '';
    const mbSize = file.size / (1024 * 1024);
    return mbSize.toFixed(2) + ' mb';
  }

  const onTrashVideo = () => {
    setFile(null);
  };

  const playVideo = () => {
    videoRef && videoRef?.current?.play();
    setPlayingVideo(true);
  };

  const pauseVideo = () => {
    videoRef && videoRef?.current?.pause();
    setPlayingVideo(false);
  };

  const canDelete = (id?: string): boolean => {
    let canDelete = true;

    if (id) {
      const trgItem = tree.find(item => item.parent === id);

      if (trgItem) canDelete = false;
    }

    return canDelete;
  };

  const handleCloseCamera = () => {
    setOpenCamera(false);
  };

  const handleSubmitCamera = (file: File, url: string) => {
    setFile(
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setValue('BlockVideoName', url);
    setValue('BlockVideoPath', url);
    setOpenCamera(false);
  };

  const handleDeleteSelectedFile = () => {
    setFile(undefined);
  };

  const [GenerateUploadUrlRequest, GenerateUploadUrlRequestStatus] = useGenerateUploadUrlMutation();

  const [GetCdnFileInfoRequest, GetCdnFileInfoStatus] = useGetCdnFileInfoMutation();

  function bytesToMegabytes(bytes: number) {
    return bytes / (1024 * 1024); // 1 MB = 1024 * 1024 bytes
  }

  const EnterpriseVideoUpload = (file: File): Promise<{ cdnPath: string; originalName: string }> => {
    return new Promise((resolve, reject) => {
      const fileName = file?.name || 'video.mp4'; // fallback if needed
      const isPrivate = false;

      GenerateUploadUrlRequest({
        fileName: (profile?.user_identification ? profile.user_identification : '') + '_' + fileName,
        isPrivate: isPrivate,
      })
        .unwrap()
        .then((responseUrl: any) => {
          const myHeaders = new Headers();
          myHeaders.append('Content-Type', 'application/octet-stream');

          const requestOptions: RequestInit = {
            method: 'PUT',
            headers: myHeaders,
            body: file,
            redirect: 'follow',
          };

          fetch(responseUrl.url, requestOptions)
            .then(() => {
              GetCdnFileInfoRequest({
                fileName: responseUrl?.file_name,
                sizeMB: bytesToMegabytes(file.size),
              })
                .unwrap()
                .then(res => {
                  if (res?.element?.isDeleted == false) {
                    resolve({
                      cdnPath: responseUrl?.cdn_pat,
                      originalName: responseUrl?.file_name,
                    });
                  } else {
                    reject(new Error('Upload failed because your file is too large.'));
                  }
                })
                .catch(err => {
                  reject(new Error(err?.data?.message || err?.message || t('MacBotMediaForm/key13')));
                });
            })
            .catch(err => {
              reject(new Error(err?.data?.message || err?.message || t('MacBotMediaForm/key13')));
            });
        })
        .catch((err: any) => {
          reject(new Error(err?.data?.message || err?.message || 'Error generating upload URL, please contact support!'));
        });
    });
  };

  const NormalVideoUpload = (file: File): Promise<{ cdnPath: string; originalName: string }> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('files', file, user_identification + '_' + file.name);

      cdnUpload(formData)
        .unwrap()
        .then(result => {
          const path = result[0].cdnPath;
          const name = result[0].original_name;
          resolve({
            cdnPath: path,
            originalName: name,
          });
        })
        .catch(err => {
          reject(err?.data?.message || err?.message || 'Upload process failure. Please contact the support!');
        });
    });
  };

  const VideoUploadManager = (file: File): Promise<{ cdnPath: string; originalName: string }> => {
    if (fileMaxSize > DEFAULT_VIDEO_SIZE_BYTES) {
      return EnterpriseVideoUpload(file);
    } else {
      return NormalVideoUpload(file);
    }
  };

  const onSubmit = (data: any) => {
    if (!file)
      return setError('BlockVideoPath', {
        type: 'required',
        message: t('MacBotMediaForm/key12'),
      });

    if (itemData && file.preview && file.preview === itemData.path) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.VideoBlock,
        title: data.BlockTitle,
        parent: itemData.parent,
        path: itemData.path,
        notes: data.notes,
      };

      changeScenarioBlock(_data, true);

      setShow?.(false);

      return;
    }
    setUploadLoading(true);

    VideoUploadManager(file)
      .then(result => {
        const path = result.cdnPath;
        const name = result.originalName;
        if (itemData) {
          const _data = {
            ...itemData,
            id: itemData.id,
            type: BlockType.VideoBlock,
            title: data.BlockTitle,
            path: path,
            name: name,
            parent: itemData.parent,
            notes: data.notes,
          };

          changeScenarioBlock(_data, file);

          setUploadLoading(false);
          setShow?.(false);
        } else {
          const _data = {
            type: BlockType.VideoBlock,
            title: data.BlockTitle,
            path: path,
            name: name,
            parent: parentId,
            notes: data.notes,
          };

          createScenarioBlock(_data, file);

          reset();

          setUploadLoading(false);

          setShow?.(false);
        }
      })
      .catch(err => {
        setError('BlockVideoPath', {
          type: 'custom',
          message: err?.data?.message || err?.message || t('MacBotMediaForm/key13'),
        });

        toast.error(err?.data?.message || err?.message || t('MacBotMediaForm/key13'), {
          delay: 100,
        });

        reset();

        setUploadLoading(false);
      });
  };
  const onDownload = () => {
    const link = document.createElement('a');
    link.href = itemData?.path;
    link.download = itemData?.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (cdnUploadResults.isLoading)
        setLoadingProcesssIndex(prevIndex => {
          if (prevIndex > 2) {
            return 0;
          }

          return prevIndex + 1;
        });
    }, 5000);

    return () => clearInterval(interval);
  }, [cdnUploadResults.isLoading]);

  //Check Show Button Delete
  useEffect(() => {
    if (itemData) {
      const find = tree?.find(dr => dr.id == itemData.id);
      if (find) {
        if (find.parent) {
          setShowDeleteButton(true);
        } else {
          setShowDeleteButton(false);
        }
      }
    }
  }, [tree, itemData]);

  useEffect(() => {
    if (!itemData) {
      setShowDeleteButton(false);
    } else {
      reset({
        BlockTitle: itemData?.title ?? '',
        BlockVideoPath: itemData?.path ?? '',
        BlockVideoName: itemData?.name ?? '',
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  useEffect(() => {
    if (openCamera) {
      pauseVideo();
    }
  }, [openCamera]);

  useEffect(() => {
    const createFileFromEditData = async (media: { path: string; name: string; size: number }) => {
      try {
        setIsLoading(true);
        const response = await fetch(itemData.path);
        const blob = await response.blob();

        const file = new File([blob], media.name, {
          type: response.headers.get('content-type') as string,
        });

        Object.assign(file, {
          preview: itemData.path,
        });

        setFile(file);
        setIsLoading(false);
        return;
      } catch (error) {
        setIsLoading(false);
        console.error('Error creating file:', error);
        return null;
      }
    };

    if (itemData?.path) createFileFromEditData(itemData?.path);
  }, [itemData?.path]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowResponseForm}
          onClose={() => {
            reset();
          }}
          scenarioType={BotTypeEnum.VIDEO_SCENARIO}
        />

        <div className="tw-flex tw-flex-col tw-w-full tw-p-6">
          <Form.Group>
            <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {t('VideoMacBotResponseForm/key1')}
            </Form.Label>
            <Form.Control
              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
              isInvalid={!!errors.BlockTitle}
              placeholder={''}
              max={30}
              maxLength={30}
              {...register(`BlockTitle`)}
            />
            <HintBlock texts={['VideoMacBotResponseForm/maxlenght']} />
            <Form.Control.Feedback type="invalid" className="d-block font-poppins">
              {errors.BlockTitle?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <hr />

          <div className="tw-flex tw-flex-col tw-gap-y-3">
            <div className="tw-text-gray-800 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{t('VideoMacBotResponseForm/l-10')}</div>

            <InfoMessageAlert
              texts={[
                t('VideoMacBotResponseForm/recommendation-title'),
                t('VideoMacBotResponseForm/format'),
                t('VideoMacBotResponseForm/resolution'),
                t('VideoMacBotResponseForm/size', {
                  size: toMegabytes(fileMaxSize),
                }),
              ]}
            />

            {isLoading && (
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-5">
                <Spinner animation="border" variant="success" />

                <span className="tw-mt-4 tw-font-semibold">{t('LoadingComponent/text')}</span>
              </div>
            )}

            <div className="tw-w-full tw-bg-gray-50 tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex ">
              {!isLoading && file?.preview && (
                <div className="tw-min-h-[88px] tw-w-full tw-p-6 tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-inline-flex">
                  <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                    <div
                      role="button"
                      tabIndex={0}
                      className="tw-justify-start tw-items-center tw-gap-3 tw-flex"
                      onClick={() => {
                        if (!file.preview.startsWith('blob:')) setShowVideo(true);
                      }}
                      onKeyDown={e => {
                        if ((e.key === 'Enter' || e.key === ' ') && !file.preview.startsWith('blob:')) setShowVideo(true);
                      }}
                    >
                      <div className="tw-w-10 tw-h-10 tw-bg-gray-200 tw-rounded tw-flex tw-flex-col tw-items-center tw-justify-center">
                        <BiVideo size={22} className="tw-text-gray-400 tw-text-xl" />
                      </div>
                      <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex">
                        <div className="xl:tw-max-w-full tw-max-w-[200px] tw-break-words   tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                          {file?.preview && file.preview ? (file.preview.length > 15 ? '...' + file.preview.slice(-15) : file.preview) : ''}
                        </div>
                        <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {file?.size && <>{(file?.size / (1024 * 1024))?.toFixed(2)} MB</>}
                        </div>
                      </div>
                    </div>
                    <div className="tw-justify-end tw-items-center tw-gap-1 tw-flex">
                      {!file.preview.startsWith('blob:') && (
                        <div className="tw-justify-end tw-items-center tw-gap-6 tw-flex tw-mr-6">
                          <BiPlay
                            className="tw-w-6 tw-h-6 tw-relative tw-cursor-pointer tw-text-gray-400"
                            onClick={() => {
                              setShowVideo(true);
                            }}
                          />

                          <BiDownload
                            className="tw-w-6 tw-h-6 tw-relative tw-cursor-pointer tw-text-gray-400"
                            onClick={() => {
                              onDownload();
                            }}
                          />
                        </div>
                      )}
                      <PSButton
                        id="btn-response-selected-file"
                        variant="danger"
                        type="button"
                        onClick={handleDeleteSelectedFile}
                        icon={<BiTrash size={22} className="tw-text-white" />}
                      />
                    </div>
                  </div>
                  {/* Fake loading */}
                  {cdnUploadResults.isLoading && (
                    <div className="tw-self-stretch tw-h-[30px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                      <div className="tw-self-stretch tw-h-1.5 tw-relative">
                        <div className="tw-h-1.5 tw-w-full  tw-bg-gray-200 tw-overflow-hidden">
                          <div className="progress tw-w-full tw-h-full tw-bg-primary left-right"></div>
                        </div>
                      </div>
                      <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                        <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {t('VideoScenarioResponseForm/key5')}
                        </div>
                        <div className="tw-grow tw-shrink tw-basis-0 tw-h-4 tw-justify-end tw-items-center tw-gap-4 tw-flex">
                          {LoadingProcessText[loadingProcesssIndex]}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!isLoading && !file?.preview && (
                <div
                  {...getRootProps({ className: 'dropzone' })}
                  className="tw-self-stretch tw-h-[156px] tw-px-4 tw-py-9 tw-bg-gray-100 tw-rounded-md tw-border-2 tw-border-dashed tw-border-gray-300 tw-flex-col tw-justify-center tw-items-center tw-gap-2 tw-flex"
                >
                  <PSInput {...getInputProps()} />
                  <div className="tw-p-2 tw-bg-gray-200 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex">
                    <BiUpload className="tw-w-6 tw-h-6 tw-relative tw-text-blue-500" />
                  </div>
                  <div className="tw-flex-col tw-justify-center tw-items-center tw-flex">
                    <div className="tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t('VideoMacBotResponseForm/l-06', {
                        size: toMegabytes(fileMaxSize),
                      })}
                    </div>
                    <div className="tw-text-center tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      {t('VideoMacBotResponseForm/l-07')}
                    </div>
                    <div className="tw-text-center tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {t('VideoMacBotResponseForm/l-11')}{' '}
                      {formatExtensions.video.map((ext, index) => (
                        <span key={ext}>
                          {ext.replace('.', '').toUpperCase()}
                          {index !== formatExtensions.video.length - 1 && ', '}
                        </span>
                      ))}
                      .
                    </div>
                  </div>
                </div>
              )}
            </div>

            {fileRejections && fileRejections.length > 0 && (
              <div className="tw-bg-gray-50">
                <div className="tw-mt-4 tw-px-6">
                  {fileRejections[0].errors.map((err: any) => {
                    if (err.code)
                      return (
                        <p key={err.code} className="tw-text-red-600 tw-text-sm tw-font-normal font-sofiaSans tw-leading-none tw-mt-5">
                          {err.code === 'file-too-large' ? t('MacBotMediaForm/key8') : t('MacBotMediaForm/key15')}
                        </p>
                      );
                  })}
                </div>
              </div>
            )}

            {!!errors.BlockVideoPath && (
              <div className="tw-text-red-600 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mt-3">{t('MacBotMediaForm/key12')}</div>
            )}
          </div>
        </div>

        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton
              id="btn-response-submit"
              type="submit"
              disabled={uploadLoading || loading || cdnUploadResults.isLoading}
              isLoading={uploadLoading || loading || cdnUploadResults.isLoading}
              title={t('MacBotResponseForm/btn-02')}
            />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>

      {openCamera && <VideoCameraComponent onClose={handleCloseCamera} onSubmit={handleSubmitCamera} />}

      {showVideo && file?.preview && (
        <ShowVideo
          show={showVideo}
          url={file?.preview}
          onClose={() => {
            setShowVideo(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

const ShowVideo = ({ show, url, onClose }: { show: boolean; url: string; onClose: () => void }) => {
  return (
    <Modal
      centered
      show={show}
      backdrop="static"
      keyboard={false}
      contentClassName="tw-max-h-[640px] tw-rounded-2xl tw-max-w-[360px] tw-relative tw-bg-transparent"
    >
      <video className="tw-w-full tw-rounded-2xl tw-border-solid" src={url} controls={true} autoPlay={true}>
        <track kind="captions" />
      </video>

      <BiX
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
        className="tw-cursor-pointer tw-w-6 tw-h-6 tw-bg-white tw-rounded-full tw-absolute tw-top-3 tw-left-3"
      />
    </Modal>
  );
};
